import React, { useState, useEffect } from 'react';
import { X, Trophy, Shield, Zap, Target, Search, CheckCircle, XCircle, AlertTriangle, ExternalLink, Eye, BarChart3 } from 'lucide-react';

import { useReputationTasks } from '../../context/ReputationTasksContext';
import apiService from '../../services/apiService';

const ContentWarfareModal = ({ isOpen, onClose, battle, onQueueTask, initialMode = 'attack' }) => {
    const { startTask } = useReputationTasks();
    const [activeTab, setActiveTab] = useState(initialMode === 'view' ? 'history' : 'attack'); // history, attack, settings, audit
    const [hoverLaunch, setHoverLaunch] = useState(false);

    // Audit State
    const [auditStatus, setAuditStatus] = useState('idle');
    const [auditData, setAuditData] = useState(null);
    const [auditLogs, setAuditLogs] = useState([]);

    const [auditStage, setAuditStage] = useState(0);
    const [auditError, setAuditError] = useState(null); // [FIX] Soft Error State

    // Reset tab when opening
    useEffect(() => {
        if (isOpen) {
            // [FIX] Support 'report' alias for Battle Report (History)
            if (initialMode === 'view' || initialMode === 'report') {
                setActiveTab('history');
            } else {
                setActiveTab('attack');
            }
        }
    }, [isOpen, initialMode]);

    // Auto-Run Audit on Open ONLY if in attack mode
    useEffect(() => {
        if (isOpen && battle && auditStatus === 'idle' && activeTab === 'attack' && initialMode !== 'view') {
            // Double check we are not in view mode to prevent race conditions
            runAudit();
        }
    }, [isOpen, battle, activeTab, initialMode]);

    const runAudit = async () => {
        setAuditStatus('loading');
        setAuditLogs(["Initializing Tactical Audit Protocol..."]);
        setActiveTab('audit'); // Switch to audit view initially
        setAuditStage(1);

        // Simulated Logs
        let stage = 1;
        const interval = setInterval(() => {
            stage++;
            setAuditStage(prev => Math.min(prev + 1, 4));
            const msg = [
                "Scanning target site structure...",
                "Extracting semantic core entities...",
                "Validating Schema.org alignment...",
                "Checking local citation consistency..."
            ][stage - 2];
            if (msg) setAuditLogs(prev => [...prev.slice(-4), msg]);
        }, 800);

        try {
            // Real API Call
            const data = await apiService.makeRequest('/reputation/audit-gap', {
                method: 'POST',
                body: JSON.stringify({
                    query: battle.keyword || battle.query || "Estate Lawyer",
                    location: battle.location || battle.profile?.location_name || battle.profile?.location_city || "Unknown Location",
                    client_profile_id: battle.profile_id || battle.profile?.id
                })
            });

            clearInterval(interval);

            // [FIX] Handle 200 OK but Empty Response
            if (!data || Object.keys(data).length === 0 || (!data.competitors && !data.primary_gap)) {
                console.warn('Audit returned empty data');
                setAuditStatus('error');
                setAuditError('No strategic data found for this battle.');
                setAuditLogs(prev => [...prev, "Audit Incomplete. No data found. Try executing manually."]);
                return;
            }

            setAuditData(data);
            setAuditStatus('complete');
            setAuditLogs(prev => [...prev, "Audit Completed. Strategy Ready."]);
            // Switch back to attack plan after successful audit
            setTimeout(() => setActiveTab('attack'), 1500);
        } catch (e) {
            clearInterval(interval);
            setAuditStatus('error');
            const cleanError = e.message.replace(/"/g, '').substring(0, 100);
            setAuditLogs(prev => [...prev, `❌ ${cleanError}`]);
        }
    };

    // [NEW] Fetch Full Details for History View
    const [fullDetails, setFullDetails] = useState(null);

    useEffect(() => {
        if (activeTab === 'history' && battle && !fullDetails) {
            fetchBattleDetails();
        }
    }, [activeTab, battle]);

    // [NEW] Phoenix Omni-Channel Strategy
    const [strategyData, setStrategyData] = useState(null);
    const [strategyLoading, setStrategyLoading] = useState(false);
    const [strategyError, setStrategyError] = useState(null); // [FIX] Strategy Error State

    useEffect(() => {
        if (activeTab === 'attack' && battle && !strategyData) {
            fetchStrategy();
        }
    }, [activeTab, battle]);

    const fetchStrategy = async () => {
        setStrategyLoading(true);
        try {
            const data = await apiService.makeRequest('/reputation/strategy/analyze', {
                method: 'POST',
                body: JSON.stringify({
                    keyword: battle.keyword || battle.query || "Estate Lawyer",
                    location: battle.location || battle.profile?.location_name || battle.profile?.location_city || "Unknown Location"
                })
            });

            // [FIX] Handle Empty/Partial Strategy
            if (!data || Object.keys(data).length === 0 || !data.decision) {
                console.warn('Strategy returned empty data');
                setStrategyError('Unable to generate attack plan. Manual intervention required.');
                return;
            }

            setStrategyData(data.decision);
        } catch (e) {
            console.error("Strategy Scout Failed", e);
            setStrategyError('Connection Error. Please retry.');
        } finally {
            setStrategyLoading(false);
        }
    };

    const fetchBattleDetails = async () => {
        try {
            const data = await apiService.makeRequest(`/reputation/battle/${battle.id}`);
            setFullDetails(data);
        } catch (e) {
            console.error("Failed to fetch battle details", e);
        }
    };

    const handleExecute = async (tactic, targetId = null) => {
        startTask(battle.id);
        if (onQueueTask) {
            // [Phoenix Omni-Channel] Pass strategy context
            const recipe = targetId
                ? { strategy: 'PHOENIX_REMASTER', target_id: targetId, ...auditData?.recipe }
                : { strategy: 'NUCLEAR_CREATION', ...auditData?.recipe };

            await onQueueTask(battle, tactic, recipe);
        }
        onClose();
    };

    if (!isOpen || !battle) return null;



    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 10000,
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            backdropFilter: 'blur(3px)'
        }} onClick={onClose}>
            <div style={{
                width: '800px', maxWidth: '95%', height: '85vh',
                background: '#f8fafc', borderRadius: '16px',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }} onClick={e => e.stopPropagation()}>

                {/* HEADER */}
                {/* HEADER - REDESIGNED */}
                <div style={{
                    padding: '24px 30px', background: 'white', borderBottom: '1px solid #e2e8f0',
                    display: 'flex', flexDirection: 'column', gap: '12px'
                }}>
                    {/* Row 1: Status & Context */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {/* 🔴 15.09.2026 (quick-260915-did): ТРИ исхода, а не два.
                                Раньше всё, что не 'WIN', печаталось как 'LOST BATTLE' —
                                и окно, открытое БЕЗ ЗАМЕРА (кнопка «исправить»),
                                уверенно сообщало о поражении, которого никто не
                                наблюдал. Отсутствие измерения ≠ проигрыш. */}
                            <span style={{
                                background: !battle.outcome ? '#f1f5f9' : battle.outcome === 'WIN' ? '#dcfce7' : '#fee2e2',
                                color: !battle.outcome ? '#475569' : battle.outcome === 'WIN' ? '#166534' : '#dc2626',
                                padding: '4px 10px',
                                borderRadius: '6px', fontSize: '0.75rem', fontWeight: '800',
                                textTransform: 'uppercase', letterSpacing: '0.05em'
                            }}>
                                {!battle.outcome ? 'ЗАМЕРА НЕТ' : battle.outcome === 'WIN' ? 'VICTORY' : 'LOST BATTLE'}
                            </span>

                            {/* Context Block: Location • Date */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#64748b', fontSize: '0.9rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span>📍</span>
                                    <span style={{ fontWeight: '500' }}>
                                        {/* [FIX] Smart Location Parser (Matches BattleCard logic) */}
                                        {(() => {
                                            let headerLocation = battle.location;
                                            if (!headerLocation || ['Local Market', 'Unknown Location', 'Unknown'].includes(headerLocation)) {
                                                // 1. Try Profile Name
                                                if (battle.profile?.company_name) {
                                                    const match = battle.profile.company_name.match(/\((.*?)\)/);
                                                    if (match && match[1]) headerLocation = match[1].trim();
                                                }
                                                // 2. Try Profile Address
                                                if ((!headerLocation || headerLocation === 'Local Market') && battle.profile?.address) {
                                                    const parts = battle.profile.address.split(',');
                                                    if (parts.length >= 2) headerLocation = parts[parts.length - 2].trim();
                                                }
                                            }
                                            // Default fallback
                                            if (!headerLocation || headerLocation === 'Local Market') headerLocation = "Local Market";
                                            return headerLocation;
                                        })()}
                                    </span>
                                </div>
                                <span style={{ color: '#cbd5e1' }}>•</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <span>📅</span>
                                    <span style={{ fontWeight: '500' }}>{new Date(battle.created_at || battle.date).toLocaleDateString()}</span>
                                </div>
                                {battle.title && battle.title.includes('[') && (
                                    <>
                                        <span style={{ color: '#cbd5e1' }}>•</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#4f46e5', fontWeight: '600', fontSize: '0.85rem', background: '#eef2ff', padding: '2px 8px', borderRadius: '4px' }}>
                                            <span>🕵️</span>
                                            {battle.title.match(/\[(.*?)\]/)?.[1] || 'Agent'}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Close Button */}
                        <button onClick={onClose} style={{
                            background: 'transparent', border: 'none', color: '#94a3b8',
                            cursor: 'pointer', padding: '4px', display: 'flex', alignItems: 'center', fontSize: '1.5rem'
                        }}>
                            ✕
                        </button>
                    </div>

                    {/* Row 2: Keyword (Large & Bold) */}
                    <div>
                        <h2 style={{ fontSize: '1.4rem', fontWeight: '800', color: '#1e293b', margin: 0, lineHeight: '1.2' }}>
                            <span style={{ color: '#94a3b8', fontSize: '0.85rem', fontWeight: '600', marginRight: '10px', verticalAlign: 'middle', textTransform: 'uppercase', letterSpacing: '0.05em' }}>KEYWORD:</span>
                            {/* [FIX] Clean Keyword Display (Strip Emojis & Persona & Time) */}
                            "{(battle.strategic_task?.query || battle.query || battle.topic || battle.keyword || battle.title || "Unknown Keyword")
                                .replace(/\[.*?\]/g, '')
                                .replace(/^[\p{Emoji}\s]+/gu, '')
                                .replace(/^Auto:\s*/i, '')
                                .replace(/\(\d{1,2}:\d{2}\)/g, '') // Remove timestamps like (02:17)
                                .replace(/\s*\(.*?\)\s*/g, '') // Catch other parentheticals safely
                                .trim()}"
                        </h2>
                    </div>
                </div>

                {/* TABS Navigation */}
                <div style={{
                    padding: '0 30px', background: 'white', borderBottom: '1px solid #e2e8f0',
                    display: 'flex', gap: '24px'
                }}>
                    {['history', 'attack', 'audit', 'settings'].map(tab => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            style={{
                                padding: '16px 0',
                                background: 'transparent',
                                border: 'none',
                                borderBottom: activeTab === tab ? '2px solid #2563eb' : '2px solid transparent',
                                color: activeTab === tab ? '#2563eb' : '#64748b',
                                fontWeight: activeTab === tab ? '700' : '500',
                                cursor: 'pointer',
                                textTransform: 'capitalize',
                                fontSize: '0.95rem',
                                display: 'flex', alignItems: 'center', gap: '8px'
                            }}
                        >
                            {tab === 'history' && <span>🕒</span>}
                            {tab === 'attack' && <span>⚡</span>}
                            {tab === 'audit' && <span>📈</span>}
                            {tab === 'settings' && <span>⚙️</span>}
                            {tab === 'attack' ? 'Attack Plan' : tab === 'history' ? 'Battle Report' : tab}
                        </button>
                    ))}
                </div>

                {/* [NUCLEAR FIX] Safety Mode: Raw Data Dump for Debugging */}
                {(activeTab === 'audit' && auditData && JSON.stringify(auditData).length < 150) && (
                    <div style={{ padding: '20px', background: '#ffe4e6', color: '#881337', borderBottom: '1px solid #f43f5e' }}>
                        <strong>⚠️ NUCLEAR DEBUG (Suspicious Data Detected):</strong>
                        <pre style={{ margin: '10px 0', whiteSpace: 'pre-wrap', fontSize: '11px' }}>{JSON.stringify(auditData, null, 2)}</pre>
                    </div>
                )}
                {(activeTab === 'attack' && strategyData && JSON.stringify(strategyData).length < 150) && (
                    <div style={{ padding: '20px', background: '#ffe4e6', color: '#881337', borderBottom: '1px solid #f43f5e' }}>
                        <strong>⚠️ NUCLEAR DEBUG (Suspicious Strategy Data):</strong>
                        <pre style={{ margin: '10px 0', whiteSpace: 'pre-wrap', fontSize: '11px' }}>{JSON.stringify(strategyData, null, 2)}</pre>
                    </div>
                )}

                {/* BODY CONTENT */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '30px' }}>

                    {activeTab === 'history' && (() => {
                        // [IRON TRUTH] 1. Extract Honest Audit Data (Google Organic)
                        const googleAudit = (fullDetails?.results || battle.results || []).find(r =>
                            r.engine === 'google_organic' && r.round_phase === 'audit'
                        );

                        // [FIX] Restore displaySnapshot for Analysis Block UI
                        const displaySnapshot = fullDetails?.serp_snapshot || battle.initial_message || battle.message_content;

                        // 2. Truth Rank (Google Only)
                        const displayRank = googleAudit ? googleAudit.rank_position : null;

                        // Ref variable for status logic
                        const myRank = displayRank;

                        // 3. Truth Competitors (Google SERP Only)
                        let realCompetitors = [];
                        if (googleAudit && googleAudit.organic_data) {
                            realCompetitors = Array.isArray(googleAudit.organic_data)
                                ? googleAudit.organic_data
                                : [];
                        }

                        // --- 2. AI OPINIONS (STATISTICS) ---
                        // Show ANY result from AI (Audit OR Attack) to provide Consensus visibility
                        const allResults = fullDetails?.results || battle.results || [];
                        const gptResult = allResults.find(r => (r.engine === 'gpt4' || r.engine === 'gpt-4'));
                        const geminiResult = allResults.find(r => r.engine === 'gemini');
                        const deepseekResult = allResults.find(r => r.engine === 'deepseek');
                        const grokResult = allResults.find(r => r.engine === 'grok');

                        // 4. Text Content
                        const aiText = fullDetails?.serp_snapshot || battle.initial_message || battle.message_content || "Анализ агента формируется или недоступен.";

                        // 5. IRON STATUS (Derived from MAX of Google & AI)
                        // [FIX] User feedback: "NOT FOUND" even if AI won.
                        // Logic: Check if ANY engine (Google, GPT, Gemini, DeepSeek, Grok) has Rank <= 10.

                        let bestRankComposite = 999;
                        if (myRank) bestRankComposite = myRank;

                        // Check AI Ranks
                        [gptResult, geminiResult, deepseekResult, grokResult].forEach(r => {
                            if (r && r.rank_position && r.rank_position < bestRankComposite) {
                                bestRankComposite = r.rank_position;
                            }
                        });


                        let overviewStatus = "NOT FOUND";
                        let overviewColor = "#dc2626"; // Red
                        let overviewIcon = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-red-500 mb-2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>; // AlertTriangle
                        let statusLabel = "Not in Top 10 ⚠️";
                        let statusBg = "#fef2f2";
                        let statusText = "#b91c1c";

                        if (bestRankComposite <= 3) {
                            overviewStatus = "DOMINANT";
                            overviewColor = "#16a34a"; // Green
                            overviewIcon = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-green-500 mb-2"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 11l2 2l4-4"></path><path d="M12 17v5"></path><path d="M2 11.5V16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4.5"></path><path d="M16 4h2a2 2 0 0 1 2 2v2A2 2 0 0 1 18 10h-2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"></path><path d="M8 4h2a2 2 0 0 1 2 2v2A2 2 0 0 1 10 10H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"></path></svg>; // Trophy
                            statusLabel = "Market Leader 🏆";
                            statusBg = "#f0fdf4";
                            statusText = "#15803d";
                        } else if (bestRankComposite <= 10) {
                            overviewStatus = "VISIBLE";
                            overviewColor = "#ca8a04"; // Yellow/Orange
                            overviewIcon = <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-8 h-8 text-yellow-500 mb-2"><path d="M2 12s3-7 10-7s10 7 10 7s-3 7-10 7s-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>; // Eye
                            statusLabel = "On First Page ⚡";
                            statusBg = "#fefce8";
                            statusText = "#b45309";
                        }

                        // Helper for Consensus Badges
                        const renderAiRankBadge = (label, result) => {
                            // [FIX] Prefer Attack Phase result if available, else Audit
                            // The logic passing 'result' here is simplistic. We should find the BEST rank.
                            // But usually we just want to know "What did DeepSeek say?"
                            // If result is null, try to find ANY result for this engine in allResults

                            let bestRes = result;
                            if (!bestRes) {
                                // Try fallback finding
                                const engineName = label.toLowerCase().includes('gpt') ? 'gpt4'
                                    : label.toLowerCase().includes('gemini') ? 'gemini'
                                        : label.toLowerCase().includes('deepseek') ? 'deepseek'
                                            : label.toLowerCase().includes('grok') ? 'grok' : '';

                                if (engineName) {
                                    // Prefer Attack > Audit
                                    bestRes = allResults.find(r => r.engine === engineName && r.round_phase === 'attack')
                                        || allResults.find(r => r.engine === engineName && r.round_phase === 'audit');
                                }
                            }

                            if (!bestRes) {
                                return (
                                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', borderRadius: '4px', border: '1px solid #f1f5f9', background: '#f8fafc', color: '#cbd5e1', fontSize: '0.75rem', fontWeight: '700' }}>
                                        <span>{label}</span>
                                        <span>---</span>
                                    </div>
                                );
                            }

                            const rank = bestRes.rank_position;
                            let colorBg = "#f1f5f9";
                            let colorText = "#64748b";
                            let colorBorder = "#e2e8f0";
                            let text = "LOST";

                            if (rank && rank <= 3) {
                                colorBg = "#dcfce7"; colorText = "#15803d"; colorBorder = "#bbf7d0";
                                text = `#${rank}`;
                            } else if (rank && rank <= 10) {
                                colorBg = "#fef9c3"; colorText = "#b45309"; colorBorder = "#fde047";
                                text = `#${rank}`;
                            } else if (rank >= 999) {
                                // [UX FIX] Show user-friendly text instead of #999
                                text = "Not in Top 10";
                            } else if (rank > 10) {
                                text = `#${rank}`;
                            }
                            // Should we show "WIN" text? No, rank is better.

                            return (
                                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 8px', borderRadius: '4px', border: `1px solid ${colorBorder}`, background: colorBg, color: colorText, fontSize: '0.75rem', fontWeight: '700' }}>
                                    <span>{label}</span>
                                    <span>{text}</span>
                                </div>
                            );
                        };

                        return (
                            <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>

                                {/* HEADER: Short Summary if needed, but we have the sidebar */}

                                <div style={{ display: 'flex', gap: '20px', flexDirection: 'row' }}>

                                    {/* --- LEFT COL: REALITY --- */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '20px' }}>

                                        {/* WINNER'S CIRCLE */}
                                        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                                            <div style={{ padding: '16px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-yellow-500"><path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"></path><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"></path><path d="M4 22h16"></path><path d="M10 11l2 2l4-4"></path><path d="M12 17v5"></path><path d="M2 11.5V16a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-4.5"></path><path d="M16 4h2a2 2 0 0 1 2 2v2A2 2 0 0 1 18 10h-2a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"></path><path d="M8 4h2a2 2 0 0 1 2 2v2A2 2 0 0 1 10 10H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z"></path></svg>
                                                <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>The Winner's Circle (Google)</h3>
                                            </div>

                                            {realCompetitors.length > 0 ? (
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                                    <thead style={{ background: '#f8fafc', color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase' }}>
                                                        <tr>
                                                            <th style={{ padding: '12px 20px', textAlign: 'left' }}>Rank</th>
                                                            <th style={{ padding: '12px 20px', textAlign: 'left' }}>Competitor</th>
                                                            <th style={{ padding: '12px 20px', textAlign: 'right' }}>Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {realCompetitors.slice(0, 5).map((comp, idx) => {
                                                            const isMe = comp.title && (comp.title.toLowerCase().includes('morgan') || (comp.link && comp.link.includes('morganlegal')));
                                                            return (
                                                                <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: isMe ? '#f0fdf4' : 'white' }}>
                                                                    <td style={{ padding: '12px 20px', fontWeight: '700', color: '#334155' }}>#{comp.position || idx + 1}</td>
                                                                    <td style={{ padding: '12px 20px', fontWeight: '600', color: '#0f172a' }}>
                                                                        {comp.title}
                                                                        {isMe && <span style={{ marginLeft: '8px', fontSize: '0.65rem', background: '#dcfce7', color: '#166534', padding: '2px 6px', borderRadius: '99px', fontWeight: '800' }}>YOU</span>}
                                                                    </td>
                                                                    <td style={{ padding: '12px 20px', textAlign: 'right' }}>
                                                                        <a href={comp.link} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#3b82f6', textDecoration: 'none', fontSize: '0.8rem', fontWeight: '600' }}>
                                                                            Inspect <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
                                                                        </a>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            ) : (
                                                <div style={{ padding: '30px', textAlign: 'center', color: '#cbd5e1', fontStyle: 'italic' }}>
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ margin: '0 auto 10px auto', display: 'block', opacity: 0.5 }}><circle cx="11" cy="11" r="8"></circle><path d="m21 21-4.3-4.3"></path></svg>
                                                    Google Organic data not available yet.
                                                </div>
                                            )}
                                        </div>

                                        {/* AGENT ANALYSIS */}
                                        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-500"><path d="M13 2L3 14h9l-1 8l10-12h-9l1-8z"></path></svg>
                                                <h3 style={{ fontSize: '0.95rem', fontWeight: '700', color: '#1e293b', margin: 0 }}>Agent Analysis</h3>
                                            </div>
                                            <div style={{ fontSize: '0.85rem', color: '#334155', lineHeight: '1.6', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                                {aiText}
                                            </div>
                                        </div>

                                    </div>

                                    {/* --- RIGHT COL: STATUS & CONSENSUS --- */}
                                    <div style={{ width: '320px', display: 'flex', flexDirection: 'column', gap: '20px' }}>

                                        {/* IRON TRUTH STATUS */}
                                        <div style={{ background: statusBg, borderRadius: '12px', padding: '24px', textAlign: 'center', border: '1px solid', borderColor: overviewColor === '#16a34a' ? '#bbf7d0' : (overviewColor === '#ca8a04' ? '#fde047' : '#fecaca') }}>
                                            <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', marginBottom: '16px', letterSpacing: '0.05em' }}>
                                                Iron Truth Status
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                                                {overviewIcon}
                                                <div style={{ fontSize: '2rem', fontWeight: '900', color: overviewColor, lineHeight: '1', marginTop: '8px' }}>{overviewStatus}</div>
                                                <div style={{ marginTop: '8px', fontSize: '0.8rem', fontWeight: '600', color: statusText, background: 'white', padding: '4px 12px', borderRadius: '99px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                                                    {statusLabel}
                                                </div>
                                            </div>
                                            <div style={{ marginTop: '20px', paddingTop: '16px', borderTop: '1px solid rgba(0,0,0,0.05)', display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>🇬 Google Organic</span>
                                                <span style={{ fontWeight: '700', color: '#1e293b' }}>{myRank ? `#${myRank}` : 'N/A'}</span>
                                            </div>
                                        </div>

                                        {/* GLOBAL AI CONSENSUS */}
                                        <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', padding: '20px' }}>
                                            <h4 style={{ fontSize: '0.75rem', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20a8 8 0 1 0 0-16a8 8 0 0 0 0 16Z"></path><path d="M12 14a2 2 0 1 0 0-4a2 2 0 0 0 0 4Z"></path><path d="M12 2v2"></path><path d="M12 20v2"></path><path d="M20 12h2"></path><path d="M2 12h2"></path><path d="m18.4 5.6l1.4-1.4"></path><path d="m6.4 18.4l-1.4 1.4"></path><path d="m5.6 6.4l-1.4-1.4"></path><path d="m18.4 18.4l1.4 1.4"></path></svg> Global AI Consensus
                                            </h4>
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                {renderAiRankBadge("🤖 GPT-4", gptResult)}
                                                {renderAiRankBadge("💎 Gemini", geminiResult)}
                                                {renderAiRankBadge("🧠 DeepSeek", deepseekResult)}
                                                {renderAiRankBadge("🚀 Grok", grokResult)}
                                            </div>
                                        </div>

                                        {/* CTA */}
                                        <button
                                            onClick={() => setActiveTab('attack')}
                                            style={{
                                                padding: '16px', width: '100%',
                                                background: '#1e293b', color: 'white',
                                                border: 'none', borderRadius: '12px', fontWeight: '700',
                                                fontSize: '1rem',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                                cursor: 'pointer', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                                                transition: 'transform 0.1s'
                                            }}
                                            onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
                                            onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
                                        >
                                            Proceed to Attack Plan <span>▶️</span>
                                        </button>

                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* --- TAB 1: ATTACK PLAN (PHOENIX OMNI-CHANNEL) --- */}
                    {activeTab === 'attack' && (
                        <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>

                            {/* LOADING STATE */}
                            {strategyLoading ? (
                                <div style={{ padding: '60px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}>
                                    <span>🌀 Loading...</span>
                                    <h3 style={{ fontSize: '1.2rem', color: '#1e293b' }}>Scouting Content Assets...</h3>
                                    <p style={{ color: '#64748b' }}>Analyzing WordPress for existing cornerstone guides.</p>
                                </div>
                            ) : strategyError ? (
                                // [FIX] Strategy Error State
                                <div style={{ padding: '40px', textAlign: 'center' }}>
                                    <div style={{ background: '#fef2f2', color: '#dc2626', padding: '20px', borderRadius: '12px', border: '1px solid #fee2e2', display: 'inline-block' }}>
                                        <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⚠️</div>
                                        <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem' }}>Strategy Generation Failed</h3>
                                        <p style={{ margin: 0, fontSize: '0.9rem', opacity: 0.9 }}>{strategyError}</p>
                                    </div>
                                    <button
                                        onClick={fetchStrategy}
                                        style={{ marginTop: '20px', padding: '10px 20px', background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', color: '#475569' }}
                                    >
                                        Retry Analysis
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* STRATEGY: PHOENIX REMASTER */}
                                    {strategyData?.strategy === 'PHOENIX_REMASTER' ? (
                                        <>
                                            {/* Icon */}
                                            <div style={{
                                                width: '80px', height: '80px', borderRadius: '50%',
                                                background: '#ffedd5', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                marginBottom: '24px', border: '2px solid #fdba74', fontSize: '2.5rem'
                                            }}>
                                                <span>🔥</span>
                                            </div>

                                            <h3 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1e293b', marginBottom: '8px' }}>
                                                Phoenix Protocol Active
                                            </h3>

                                            <div style={{ fontSize: '1rem', color: '#64748b', marginBottom: '30px' }}>
                                                <span style={{ marginRight: '10px' }}>Strategy: <strong style={{ color: '#ea580c' }}>Remaster</strong></span>
                                                <span>Asset Found: <strong style={{ color: '#1e293b' }}>{String(strategyData?.target_post?.date || '').substring(0, 4) || 'Unknown'}</strong></span>
                                            </div>

                                            {/* Action Plan Box */}
                                            <div style={{
                                                width: '100%',
                                                background: '#fff7ed', border: '1px solid #fdba74', borderRadius: '12px',
                                                padding: '24px', textAlign: 'left', marginBottom: '30px',
                                                boxShadow: '0 4px 6px -1px rgba(251, 146, 60, 0.1)'
                                            }}>
                                                <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#9a3412', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <span>✅</span> Asset Identified:
                                                </h4>
                                                <div style={{ marginBottom: '16px', padding: '12px', background: 'white', borderRadius: '8px', border: '1px solid #fed7aa' }}>
                                                    <p style={{ margin: 0, fontWeight: '600', color: '#1e293b' }}>{strategyData?.target_post?.title || 'Untitled Asset'}</p>
                                                    <a href={strategyData?.target_post?.link || '#'} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85rem', color: '#f97316', textDecoration: 'none' }}>View Original ↗</a>
                                                </div>
                                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#9a3412', lineHeight: '1.6', fontSize: '0.95rem' }}>
                                                    <li>Preserves URL Authority & Age.</li>
                                                    <li>Upgrades content to v2026 Standards.</li>
                                                    <li>Injects new competitor analysis.</li>
                                                </ul>
                                            </div>

                                            {/* Buttons */}
                                            <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                                                <button
                                                    onClick={onClose}
                                                    style={{
                                                        flex: 1, padding: '14px',
                                                        background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px',
                                                        color: '#334155', fontWeight: '600', fontSize: '1rem',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => handleExecute('remaster', strategyData?.target_post?.id)}
                                                    style={{
                                                        flex: 1, padding: '14px',
                                                        background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                                                        border: 'none', borderRadius: '8px',
                                                        color: 'white', fontWeight: '700', fontSize: '1rem',
                                                        cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                                        boxShadow: '0 4px 12px rgba(234, 88, 12, 0.3)'
                                                    }}
                                                >
                                                    <span>🔥</span> Ignite Phoenix
                                                </button>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            {/* STRATEGY: NUCLEAR CREATION (Default) */}
                                            {/* Icon */}
                                            <div style={{
                                                width: '80px', height: '80px', borderRadius: '50%',
                                                background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                marginBottom: '24px', fontSize: '2.5rem'
                                            }}>
                                                <span>☢️</span>
                                            </div>

                                            <h3 style={{ fontSize: '1.8rem', fontWeight: '800', color: '#1e293b', marginBottom: '8px' }}>
                                                Nuclear Option
                                            </h3>

                                            <div style={{ fontSize: '1rem', color: '#64748b', marginBottom: '30px' }}>
                                                <span style={{ marginRight: '10px' }}>Strategy: <strong style={{ color: '#475569' }}>Create New</strong></span>
                                                <span>Location: <strong style={{ color: '#475569' }}>{battle.location || battle.profile?.location_city || 'Unknown'}</strong></span>
                                            </div>

                                            {/* Action Plan Box */}
                                            <div style={{
                                                width: '100%',
                                                background: 'white', border: '1px solid #e2e8f0', borderRadius: '12px',
                                                padding: '24px', textAlign: 'left', marginBottom: '30px',
                                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)'
                                            }}>
                                                <h4 style={{ fontSize: '1rem', fontWeight: '700', color: '#334155', marginBottom: '12px' }}>
                                                    AI Action Plan:
                                                </h4>
                                                <ul style={{ margin: 0, paddingLeft: '20px', color: '#475569', lineHeight: '1.6', fontSize: '0.95rem' }}>
                                                    <li>No existing content found.</li>
                                                    <li>Create fresh Cornerstone Guide ({'>'}2000w).</li>
                                                    <li>Target Gap: <strong>{battle.keyword || battle.query || 'Content Gap'}</strong></li>
                                                    <li><strong>Objective:</strong> Dominate SERP.</li>
                                                </ul>
                                            </div>

                                            {/* Buttons */}
                                            <div style={{ display: 'flex', gap: '16px', width: '100%' }}>
                                                <button
                                                    onClick={onClose}
                                                    style={{
                                                        flex: 1, padding: '14px',
                                                        background: 'white', border: '1px solid #cbd5e1', borderRadius: '8px',
                                                        color: '#334155', fontWeight: '600', fontSize: '1rem',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    onClick={() => handleExecute('nuclear')}
                                                    style={{
                                                        flex: 1, padding: '14px',
                                                        background: '#1e293b', border: 'none', borderRadius: '8px',
                                                        color: 'white', fontWeight: '700', fontSize: '1rem',
                                                        cursor: 'pointer',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
                                                    }}
                                                >
                                                    Confirm & Execute
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    {/* --- TAB 2: AUDIT --- */}
                    {activeTab === 'audit' && (
                        <div>
                            <div style={{
                                padding: '20px', background: 'white', borderRadius: '12px',
                                border: '1px solid #e2e8f0', marginBottom: '20px'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                                    {auditStatus === 'loading' ? (
                                        <span>🌀</span>
                                    ) : (
                                        <span>✅</span>
                                    )}
                                    <div>
                                        <div style={{ fontWeight: '700', color: '#1e293b' }}>
                                            {auditStatus === 'loading' ? 'Running Tactical Analysis...' : 'Intelligence Report Ready'}
                                        </div>
                                        <div style={{ fontSize: '0.85rem', color: '#64748b' }}>
                                            Scanning competitors in {battle.location} for semantic gaps.
                                        </div>
                                    </div>
                                </div>

                                {/* Log Console */}
                                <div style={{
                                    background: '#0f172a', borderRadius: '8px', padding: '16px',
                                    height: '150px', overflowY: 'auto', fontFamily: 'monospace', fontSize: '0.8rem'
                                }}>
                                    {auditLogs.map((log, i) => (
                                        <div key={i} style={{ color: '#38bdf8', marginBottom: '4px' }}>
                                            <span style={{ opacity: 0.5, marginRight: '8px' }}>{new Date().toLocaleTimeString().split(' ')[0]}</span>
                                            {log}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* [FIX] Error State UI */}
                            {auditError && (
                                <div style={{ padding: '20px', background: '#fef2f2', borderRadius: '12px', border: '1px solid #fee2e2', marginBottom: '20px' }}>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626', margin: '0 0 10px 0' }}>
                                        <span>⚠️</span> Audit Warning
                                    </h4>
                                    <p style={{ fontSize: '0.9rem', color: '#b91c1c' }}>
                                        {auditError}
                                    </p>
                                    <button
                                        onClick={() => setActiveTab('attack')}
                                        style={{ marginTop: '12px', padding: '8px 16px', background: 'white', border: '1px solid #fca5a5', color: '#dc2626', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                                    >
                                        Proceed to Manual Fix ➔
                                    </button>
                                </div>
                            )}

                            {!auditError && auditData && (
                                <div style={{ padding: '20px', background: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                                    <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', margin: '0 0 10px 0' }}>
                                        <span>🛡️</span> Opportunity Detected
                                    </h4>
                                    <p style={{ fontSize: '0.9rem', color: '#166534' }}>
                                        <strong>Primary Gap:</strong> {auditData.primary_gap}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* --- TAB 3: SETTINGS --- */}
                    {activeTab === 'settings' && (
                        <div>
                            <div className="card" style={{ padding: '20px', border: '1px solid #e2e8f0' }}>
                                <h3 style={{ marginBottom: '15px' }}>Configuration</h3>
                                <p style={{ color: '#64748b' }}>Advanced settings for this specific battle.</p>
                                {/* Placeholder for settings */}
                                <div style={{ marginTop: '20px', padding: '15px', background: '#f8fafc', borderRadius: '8px' }}>
                                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>Target Word Count</label>
                                    <select style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                                        <option>2,500 Words (Standard)</option>
                                        <option>4,000 Words (Skyscraper)</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div >
        </div >
    );
};

// [FIX] Error Boundary to prevent White Screen of Death
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        this.setState({ error, errorInfo });
        console.error("Uncaught error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{ padding: 20, background: '#fee2e2', color: '#b91c1c', zIndex: 99999, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, overflow: 'auto' }}>
                    <h1>⚠️ Critical UI Crash</h1>
                    <details style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                        {this.state.error && this.state.error.toString()}
                        <br />
                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                    </details>
                    <button onClick={() => window.location.reload()} style={{ marginTop: 20, padding: '10px 20px', background: '#b91c1c', color: 'white', border: 'none', borderRadius: 4 }}>
                        Reload Application
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

// Wrap export
const ContentWarfareModalWrapped = (props) => (
    <ErrorBoundary>
        <ContentWarfareModal {...props} />
    </ErrorBoundary>
);

export default ContentWarfareModalWrapped;
